import React, { useCallback, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Button, Flex, InfoAccordion, InfoIcon, Text, TextVariant } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import { Grid } from '@mui/material'
import isNil from 'lodash/isNil'
import dayjs from 'dayjs'
import { BigNumber } from 'ethers'
import { BNToNumberWithDecimals } from '@mprolab/sdk'
import UseAnimations from 'react-useanimations'
import loading from 'react-useanimations/lib/loading'
import Image from 'components/CompatImage'
import useScreenSize from 'hooks/useScreenSize'
import useToast from 'hooks/useToast'
import { useSetupNetwork } from 'hooks/useSetupNetwork'
import { useConnectPanel } from 'contexts/ConnectPanel'
import { tokenLogoSrc } from 'utils/tokenLogoUri'
import { formatPrice } from '../stake.logic'
import ColumnComponent from '../components/ColumnComponent'
import { useFeeVault } from './feeVault.logic'
import { sentenceFor } from './feeVault.rules'

/*
 * The Fee Vault, first on the Stakes page, in the anatomy of every other
 * stake row: the section container, an accordion with a summary row, and a
 * detail box underneath. It never expires, so there are no Active/Inactive
 * tabs — one permanent row.
 *
 * The bar on top is the operator's ask, said plainly: what the community has
 * earned together since the vault opened, how much of it holders have already
 * taken, and how much is waiting.
 */

const Container = styled(Flex)`
  width: 100%;
  padding: 24px;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.backgroundContrast};
`

const Divider = styled(Flex)`
  margin: 8px 0 16px 0;
  opacity: 0.2;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray6};
`

const TotalsBar = styled(Flex)`
  width: 100%;
  gap: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.gray11};
  justify-content: space-between;
  align-items: stretch;
  margin-bottom: 16px;
  ${({ theme }) => theme.mediaQueries.mobile} {
    flex-direction: column;
  }
`

const TotalCell = styled(Flex)`
  flex-direction: column;
  gap: 4px;
  flex: 1;
`

const Progress = styled.div<{ $pct: number }>`
  position: relative;
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.gray9};
  overflow: hidden;
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    width: ${({ $pct }) => Math.max(0, Math.min(100, $pct))}%;
    background: ${({ theme }) => theme.colors.cyan};
  }
`

const TickerWrapper = styled(Flex)`
  align-items: center;
  gap: 8px;
  width: max-content;
  border-radius: 24px;
  padding: 8px 13px 8px 8px;
  background: ${({ theme }) => theme.colors.gray10};
`

const BorderContainerContrast = styled(Flex)`
  width: 100%;
  padding: 8px 16px;
  border-radius: 8px;
  min-height: 64px;
  background: ${({ theme }) => theme.colors.gray11};
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  ${({ theme }) => theme.mediaQueries.mobile} {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 8px 12px;
  }
`

const Box = styled(Flex)`
  width: 100%;
  background: ${({ theme }) => theme.colors.gray10};
  border-radius: 8px;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  gap: 24px;
  ${({ theme }) => theme.mediaQueries.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`

/** A holder may own hundreds of licences; the list shows this many rows and scrolls the rest. */
const VISIBLE_ROWS = 5
const ROW_HEIGHT = 46

const LicenceList = styled.div`
  max-height: ${VISIBLE_ROWS * ROW_HEIGHT}px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: 4px;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.gray8} transparent;
`

const LicenceRow = styled(Flex)`
  width: 100%;
  height: ${ROW_HEIGHT}px;
  box-sizing: border-box;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray10};
  gap: 12px;
  &:last-child {
    border-bottom: none;
  }
`

const Muted = styled(Text)`
  color: ${({ theme }) => theme.colors.gray6};
`

const mpro = (v?: BigNumber | null) => (isNil(v) ? '-' : `${formatPrice(BNToNumberWithDecimals(v, 18, 6))} MPRO`)
const shortWallet = (w: string) => (w ? `${w.slice(0, 6)}…${w.slice(-4)}` : '')

const FeeVault: React.FC = () => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { isMobile } = useScreenSize()
  const { toastSuccess, toastError } = useToast()
  const { setupNetwork } = useSetupNetwork()
  const { setConnectPanelOpen } = useConnectPanel()
  const v = useFeeVault()
  // Which slice of the licence list is on screen — "Showing 6–10 of 20" follows the scroll.
  const [firstVisible, setFirstVisible] = useState(0)
  const onListScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setFirstVisible(Math.round(e.currentTarget.scrollTop / ROW_HEIGHT))
  }, [])

  const claimedPct = useMemo(() => {
    if (!v.totals || v.totals.collected.isZero()) return 0
    return v.totals.claimed.mul(10000).div(v.totals.collected).toNumber() / 100
  }, [v.totals])

  // The vault is registered per environment; without a registration there is
  // nothing to show and nothing to pretend.
  if (!v.configured) return null

  const wallet = v.walletView
  const canClaim =
    v.signedIn &&
    !!wallet &&
    wallet.verdict.ok &&
    !isNil(wallet.previewTotal) &&
    wallet.previewTotal.gt(0) &&
    !v.totals?.paused &&
    !v.claiming

  const onClaim = async () => {
    if (!v.onRightChain) {
      await setupNetwork({ chainId: v.chainId as number, modal: true, error: false })
      return
    }
    const outcome = await v.claim()
    if (outcome.status === 'confirmed') {
      toastSuccess(t('feeVault.claimed'), t('feeVault.claimedDetail', { amount: mpro(outcome.total) }))
    } else if (outcome.status === 'refused') {
      toastError(t('feeVault.claimFailed'), sentenceFor(outcome.errorName, t))
    }
  }

  /** The one line under the claim button that says why it is off. */
  const whyNot = (): string | null => {
    if (!v.signedIn) return t('feeVault.signInToSee')
    if (!wallet) return null
    if (v.totals?.paused) return t('feeVault.errors.paused')
    const verdict = wallet.verdict
    // Equality, not truthiness: this tsconfig does not narrow a literal
    // discriminant on `!x.ok`, and the union needs narrowing to read `reason`.
    if (verdict.ok === false) {
      return verdict.reason === 'unmapped'
        ? t('feeVault.errors.unmapped', { known: verdict.known, total: verdict.total })
        : t('feeVault.errors.chainDiffers', { known: verdict.known, total: verdict.total })
    }
    if (wallet.previewError) return sentenceFor(wallet.previewError, t)
    if (wallet.ids.length === 0) return t('feeVault.noLicences')
    if (wallet.previewTotal?.isZero()) return t('feeVault.nothingYet')
    return null
  }

  const summary = (
    <Grid container display="flex" alignItems="center" padding="8px">
      <Grid item xs={isMobile ? 12 : 2.4}>
        <TickerWrapper>
          <Image src={tokenLogoSrc(undefined)} width={16} height={16} />
          <Text variant={TextVariant.BODY_SMALL_BOLD}>{t('feeVault.name')}</Text>
        </TickerWrapper>
      </Grid>
      <Grid item xs={isMobile ? 6 : 2.4}>
        <Muted variant={TextVariant.BODY_SMALL}>{t('feeVault.source')}</Muted>
        <Text variant={TextVariant.BODY_SMALL}>
          {t('feeVault.sourceValue')}
          <InfoIcon
            iconProps={{ size: 13, style: { marginLeft: 6, verticalAlign: 'middle' } }}
            tooltipProps={{ title: t('feeVault.tooltip.vault') }}
          />
        </Text>
      </Grid>
      <Grid item xs={isMobile ? 6 : 2.4}>
        <Muted variant={TextVariant.BODY_SMALL}>{t('feeVault.shares')}</Muted>
        <Text variant={TextVariant.BODY_SMALL}>{v.totals ? v.totals.shareCount.toLocaleString() : '-'}</Text>
      </Grid>
      <Grid item xs={isMobile ? 6 : 2.4}>
        <Muted variant={TextVariant.BODY_SMALL}>{t('feeVault.yourLicences')}</Muted>
        <Text variant={TextVariant.BODY_SMALL}>{v.signedIn && wallet ? wallet.ids.length : '-'}</Text>
      </Grid>
      <Grid item xs={isMobile ? 6 : 2.4}>
        <Muted variant={TextVariant.BODY_SMALL}>{t('feeVault.yourClaimable')}</Muted>
        <Text variant={TextVariant.BODY_SMALL_BOLD} color={colors.cyan}>
          {v.signedIn && wallet ? mpro(wallet.previewTotal ?? BigNumber.from(0)) : '-'}
        </Text>
      </Grid>
    </Grid>
  )

  return (
    <Container>
      <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="8px">
        <Text variant={TextVariant.BODY_DEFAULT_BOLD}>
          {t('feeVault.title')}
          <InfoIcon
            iconProps={{ size: 14, style: { marginLeft: 6, verticalAlign: 'middle' } }}
            tooltipProps={{ title: t('feeVault.tooltip.ledger') }}
          />
        </Text>
        {v.unavailable ? <Muted variant={TextVariant.BODY_SMALL}>{t('feeVault.unavailable')}</Muted> : null}
      </Flex>
      <Divider />

      {/* The community's number: earned together, taken out, waiting. */}
      <TotalsBar>
        <TotalCell>
          <Muted variant={TextVariant.BODY_SMALL}>{t('feeVault.collectedTotal')}</Muted>
          <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{mpro(v.totals?.collected)}</Text>
        </TotalCell>
        <TotalCell>
          <Muted variant={TextVariant.BODY_SMALL}>{t('feeVault.claimedTotal')}</Muted>
          <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{mpro(v.totals?.claimed)}</Text>
          <Progress $pct={claimedPct} />
          <Muted variant={TextVariant.TEXT_XS_REGULAR}>
            {t('feeVault.claimedPct', { pct: claimedPct.toFixed(1) })}
          </Muted>
        </TotalCell>
        <TotalCell>
          <Muted variant={TextVariant.BODY_SMALL}>{t('feeVault.heldTotal')}</Muted>
          <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{mpro(v.totals?.held)}</Text>
        </TotalCell>
      </TotalsBar>

      {v.loading && !v.totals ? (
        <Flex fullWidth justifyContent="center" minHeight="100px" alignItems="center">
          <UseAnimations animation={loading} strokeColor={colors.gray6} size={48} />
        </Flex>
      ) : (
        <InfoAccordion withBorder defaultExpanded description={summary}>
          <BorderContainerContrast mt="16px">
            <Flex>
              <ColumnComponent
                title={t('feeVault.heldTotal')}
                value={mpro(v.totals?.held)}
                subValue={t('feeVault.sharesEqual', { count: v.totals?.shareCount ?? 0 })}
                subValueAligned
              />
            </Flex>
            <Flex fullWidth={isMobile}>
              {!v.signedIn ? (
                <Box>
                  <ColumnComponent
                    rightAligned
                    subValueAligned
                    title={t('feeVault.yourClaimable')}
                    value={t('feeVault.signInToSee')}
                  />
                  <Button size="sm" onClick={() => setConnectPanelOpen(true)}>
                    {t('stakingPanel.connectWallet')}
                  </Button>
                </Box>
              ) : (
                <Box>
                  <ColumnComponent
                    rightAligned={!isMobile}
                    title={t('feeVault.yourClaimable')}
                    value={mpro(wallet?.previewTotal ?? BigNumber.from(0))}
                    subValue={t('feeVault.acrossLicences', { count: wallet?.ids.length ?? 0 })}
                    isLoading={v.licencesLoading}
                  />
                  <Flex flexDirection="column" gap="6px" alignItems={isMobile ? 'stretch' : 'flex-end'}>
                    <Button size="sm" fullWidth={isMobile} disabled={!canClaim && v.onRightChain} onClick={onClaim}>
                      {!v.onRightChain
                        ? t('stakingPanel.switchNetwork')
                        : v.claiming
                        ? t('feeVault.claiming')
                        : t('feeVault.claimAll')}
                    </Button>
                    {whyNot() ? <Muted variant={TextVariant.TEXT_XS_REGULAR}>{whyNot()}</Muted> : null}
                  </Flex>
                </Box>
              )}
            </Flex>
          </BorderContainerContrast>

          {/* Per licence: the ledger the contract keeps. */}
          {v.signedIn && wallet && wallet.rows.length > 0 ? (
            <Flex flexDirection="column" mt="12px" px="16px" gap="6px">
              <Muted variant={TextVariant.TEXT_XS_REGULAR}>
                {wallet.rows.length > VISIBLE_ROWS
                  ? t('feeVault.showingRows', {
                      from: Math.min(firstVisible, wallet.rows.length - VISIBLE_ROWS) + 1,
                      to: Math.min(firstVisible + VISIBLE_ROWS, wallet.rows.length),
                      total: wallet.rows.length
                    })
                  : t('feeVault.showingAll', { total: wallet.rows.length })}
              </Muted>
              <LicenceList onScroll={onListScroll}>
                {wallet.rows.map((r) => (
                  <LicenceRow key={r.id}>
                    <Flex flexDirection="column">
                      <Text variant={TextVariant.BODY_SMALL}>#{r.id}</Text>
                      {!r.isShare ? (
                        <Muted variant={TextVariant.TEXT_XS_REGULAR}>{t('feeVault.pendingAdmission')}</Muted>
                      ) : r.lastClaimAt ? (
                        <Muted variant={TextVariant.TEXT_XS_REGULAR}>
                          {t('feeVault.lastClaimed', {
                            date: dayjs.unix(r.lastClaimAt).format('D MMM YYYY, HH:mm'),
                            wallet: shortWallet(r.lastClaimedBy)
                          })}
                        </Muted>
                      ) : (
                        <Muted variant={TextVariant.TEXT_XS_REGULAR}>{t('feeVault.neverClaimed')}</Muted>
                      )}
                    </Flex>
                    <Flex flexDirection="column" alignItems="flex-end">
                      <Text variant={TextVariant.BODY_SMALL_BOLD}>{r.isShare ? mpro(r.claimable) : '-'}</Text>
                      {r.claimed.gt(0) ? (
                        <Muted variant={TextVariant.TEXT_XS_REGULAR}>
                          {t('feeVault.claimedSoFar', { amount: mpro(r.claimed) })}
                        </Muted>
                      ) : null}
                    </Flex>
                  </LicenceRow>
                ))}
              </LicenceList>
            </Flex>
          ) : null}
        </InfoAccordion>
      )}
    </Container>
  )
}

export default FeeVault
